import ButtonIconT from '@components/tailwind-components/Button/ButtonIconT';
import {
  type Control,
  type UseFormSetValue,
  useFieldArray,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import WebclientMenuList from './WebclientMenuList';

type Props = {
  name: string;
  watch: any;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  selectedRevisionId: string;
  // We are using this component in some places where we need to pass additional styles for the inputs and their labels
  inputClassName?: string;
  labelClassName?: string;
};

// * Arbitrary limit, discuss with the team whether to limit and how many items we want
const MENU_ENTRY_LIMIT = 5;

export const WebclientMenu: React.FC<Props> = ({
  watch,
  control,
  name,
  setValue,
  selectedRevisionId,
  labelClassName = '',
  inputClassName = '',
}) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as any,
  });

  const handleAddEntry = () => {
    const newEntryIndex = fields.length;
    append({ title: {}, type: 'payload', payload: '' });
    // useFieldArray re-applies defaultValues by index, so re-using an index
    // after a delete would otherwise resurrect the removed entry's values.
    // Clearing the new entry's fields explicitly keeps it empty.
    setValue(`${name}.${newEntryIndex}.title.en`, '');
    setValue(`${name}.${newEntryIndex}.payload`, '');
    setValue(`${name}.${newEntryIndex}.type`, 'payload');
  };

  return (
    <div>
      <div className='!text-primary-base mt-3 text-base'>
        {t('settings.webclient.config.form.config.menu.label')}
      </div>
      {fields.map((field, actionId) => (
        <div key={field.id} className='flex flex-col gap-2'>
          <WebclientMenuList
            className='flex-grow-1 mt-3'
            name={`${name}.${actionId}`}
            control={control}
            watch={watch}
            setValue={setValue}
            selectedRevisionId={selectedRevisionId}
            inputClassName={inputClassName}
            labelClassName={labelClassName}
          />
          {fields.length > 0 && (
            <ButtonIconT
              icon='delete'
              onClick={() => remove(actionId)}
              label={t(
                'settings.webclient.config.form.config.menu.delete-action',
              )}
              labelClassName='text-xs'
              color='danger'
              iconSize='xl'
            />
          )}
        </div>
      ))}
      {fields.length < MENU_ENTRY_LIMIT && (
        <ButtonIconT
          className='mt-3'
          icon='add_circle'
          onClick={handleAddEntry}
          label={t('settings.webclient.config.form.config.menu.add-action')}
          labelClassName='text-xs'
          color='primary-base'
          iconSize='xl'
        />
      )}
    </div>
  );
};
